import { Modal } from '@property.com.au/ui-kit';
import { useFeatureRollout } from '@rea-argonaut/experiment';
import type { FunctionComponent } from 'react';
import { useTheme } from 'styled-components';
import { mapDynamicContentItems } from '../../../app-kit/components/DynamicContent/helpers/mapDynamicContentItem';
import { convertHexStringToRGBA } from '../../../app-kit/helpers/convertHexStringToRGBA';
import { getCssVariableValue } from '../../../app-kit/helpers/getCssVariableValue';
import { mapApiWeblinkIconInfoToLinkIconInfo } from '../../../app-kit/helpers/mapApiWeblinkIconInfoToLinkIconInfo';
import { mapApiWeblinkVariantToLinkVariant } from '../../../app-kit/helpers/mapApiWeblinkVariantToLinkVariant';
import { useBeforeYouBuyModalQuery } from '../../graphql/useBeforeYouBuyModalQuery';
import { usePropertyPageQuery } from '../../graphql/usePropertyPageQuery';
import { BeforeYouBuyModalContent } from './subcomponents/BeforeYouBuyModalContent/BeforeYouBuyModalContent';
import type { BeforeYouBuyModalDialogProps } from './types';

interface BeforeYouBuyModalProps extends BeforeYouBuyModalDialogProps {
  opened: boolean;
  onClose: () => void | Promise<void>;
}

const BeforeYouBuyModal: FunctionComponent<BeforeYouBuyModalProps> = ({
  title,
  propertyAddress,
  beforeYouBuyLink,
  featureTitle,
  featureList,
  featureListV2,
  eta,
  priceBlock,
  opened,
  onClose,
  loading,
}) => {
  const theme = useTheme();
  const colourValue = getCssVariableValue(theme.palette.backgroundOverlayStatic);
  const backgroundColor = convertHexStringToRGBA(colourValue, 0.3);

  return (
    <Modal
      opened={opened}
      onRequestClose={() => void onClose()}
      getAppElement={() => '#argonaut-wrapper'}
      reactModalProps={{
        style: {
          overlay: {
            backgroundColor,
          },
        },
      }}
    >
      <BeforeYouBuyModalContent
        title={title}
        propertyAddress={propertyAddress}
        beforeYouBuyLink={beforeYouBuyLink}
        featureTitle={featureTitle}
        featureList={featureList}
        featureListV2={featureListV2}
        eta={eta}
        priceBlock={priceBlock}
        onClose={onClose}
        loading={loading}
      />
    </Modal>
  );
};

export const BeforeYouBuyModalWithData: FunctionComponent<{
  opened: boolean;
  onClose: () => void | Promise<void>;
}> = ({ opened, onClose }) => {
  const isBYBLegalDisclaimersEnabled = useFeatureRollout('byb_legal_disclaimers').isEnabled();
  const { data, fetching } = useBeforeYouBuyModalQuery();
  const cachedPropertyPage = usePropertyPageQuery();
  const beforeYouBuyModalDataSSR = cachedPropertyPage?.data.propertyPageBySlugV3.beforeYouBuyModal;
  const beforeYouBuyV2ModalDataCSR = data?.beforeYouBuyV2?.propertyPageModal;

  if (!beforeYouBuyModalDataSSR) return null;
  const {
    title,
    propertyAddress,
    beforeYouBuyLink,
    featureTitle,
    featureList,
    featureListV2,
    eta,
    etaV2,
    priceBlock,
  } = beforeYouBuyV2ModalDataCSR ?? beforeYouBuyModalDataSSR;

  const bybETA = isBYBLegalDisclaimersEnabled ? etaV2 : eta;

  const bybLink = {
    ...beforeYouBuyLink,
    variant: mapApiWeblinkVariantToLinkVariant(beforeYouBuyLink.variant),
    iconInfo: beforeYouBuyLink.iconInfo
      ? mapApiWeblinkIconInfoToLinkIconInfo(beforeYouBuyLink.iconInfo)
      : null,
  };

  return (
    <BeforeYouBuyModal
      opened={opened}
      onClose={onClose}
      title={title}
      propertyAddress={propertyAddress}
      beforeYouBuyLink={bybLink}
      featureTitle={featureTitle}
      featureList={featureList ?? null}
      featureListV2={featureListV2?.map(mapDynamicContentItems) ?? null}
      eta={bybETA}
      priceBlock={{ ...priceBlock, disclaimerV2: mapDynamicContentItems(priceBlock?.disclaimerV3) }}
      loading={fetching}
    />
  );
};
